/**
 * POSTAL CODE VALIDATION UTILITY
 * 
 * Key Points:
 * - Validates Dutch postal codes for delivery regions
 * - Supports wildcard patterns (e.g., "1234*") and exact matches
 * - Handles fallback regions with asterisk (*) for catch-all scenarios
 * - Special case: returns null for Waddeneilanden region (no delivery)
 * - Expects DOM element with id="regions-data" containing JSON regions data
 * - Checks timeslot availability for delivery regions
 */


// Constants
const CONSOLE_PREFIX = '[POSTAL-CODE-VALIDATION]';

// Helper Functions
/**
 * @param {string} level - Log level (log, warn, error)
 * @param {string} message - Message to log
 * @param {...any} args - Additional arguments
 */
function logMessage(level, message, ...args) {
  // /** @type {any} */ (console)[level](`🔍 ${CONSOLE_PREFIX} ${message}`, ...args);
}

/**
 * Postal code validation utility
 * Validates if a postcode belongs to a delivery region and has available timeslots
 */
class PostalCodeValidator {
  /**
   * Convert postcodes to normalized array format
   * @param {string|string[]|{value: string|string[]}} postcodes - Postcodes to normalize
   * @returns {string[]} - Array of normalized postcodes
   */
  static normalizePostcodesArray(postcodes) {
    try {
      let postcodesArray = postcodes;
      
      // Handle object with value property
      if (postcodes && typeof postcodes === 'object' && 'value' in postcodes) {
        postcodesArray = postcodes.value;
      }
      
      // Convert to array if it's a string
      if (typeof postcodesArray === 'string' && postcodesArray.includes(',')) {
        postcodesArray = postcodesArray.split(',').map(p => p.trim());
      } else if (typeof postcodesArray === 'string') {
        postcodesArray = [postcodesArray];
      }
      
      // Ensure we return an array
      const result = Array.isArray(postcodesArray) ? postcodesArray : [];
      logMessage('log', `Normalized postcodes array: ${result.length} items`);
      return result;
    } catch (error) {
      logMessage('error', 'Error normalizing postcodes array', error);
      
      return [];
    }
  }

  /**
   * Check if a postcode matches a pattern (exact or wildcard)
   * @param {string} postcode - Normalized postcode to check
   * @param {string} pattern - Pattern to match against
   * @returns {boolean} - Whether the postcode matches the pattern
   */
  static postcodeMatchesPattern(postcode, pattern) {
    try {
      const normalizedPattern = pattern.replace(/\s/g, '').toUpperCase();
      
      // Exact match
      if (normalizedPattern === postcode) {
        logMessage('log', `Exact pattern match: ${postcode} matches ${pattern}`);
        return true;
      }
      
      // Wildcard pattern matching
      if (normalizedPattern.includes('*')) {
        const patternPrefix = normalizedPattern.replace('*', '');
        const matches = patternPrefix !== '' && postcode.includes(patternPrefix);
        // logMessage('log', `Wildcard pattern match: ${postcode} vs ${pattern} = ${matches}`);
        return matches;
      }
      
      logMessage('log', `No pattern match: ${postcode} vs ${pattern}`);
      return false;
    } catch (error) {
      logMessage('error', 'Error checking postcode pattern match', error);
      
      return false;
    }
  }

  /**
   * Find a region by its system handle
   * @param {Array<{system?: {handle?: string}, postcodes?: any}>} regions - Array of regions to search
   * @param {string} handle - System handle to search for
   * @returns {{system?: {handle?: string}, postcodes?: any}|null} - Found region or null
   */
  static findRegionByHandle(regions, handle) {
    try {
      const foundRegion = regions.find(region => region.system?.handle === handle) || null;
      logMessage('log', `Region search for handle "${handle}": ${foundRegion ? 'found' : 'not found'}`);
      return foundRegion;
    } catch (error) {
      logMessage('error', 'Error finding region by handle', error);
      
      return null;
    }
  }

  /**
   * Check if postcode belongs to a specific region
   * @param {string} postcode - Normalized postcode to check
   * @param {{postcodes?: any, system?: {handle?: string}}} region - Region object to check against
   * @returns {boolean} - Whether the postcode belongs to the region
   */
  static postcodeBelongsToRegion(postcode, region) {
    try {
      if (!region.postcodes) {
        logMessage('log', `Region has no postcodes defined`);
        return false;
      }
      
      const postcodesArray = this.normalizePostcodesArray(region.postcodes);
      const belongsToRegion = postcodesArray.some(pattern => 
        this.postcodeMatchesPattern(postcode, pattern)
      );
      
      logMessage('log', `Postcode ${postcode} belongs to region: ${belongsToRegion}`);
      return belongsToRegion;
    } catch (error) {
      logMessage('error', 'Error checking if postcode belongs to region', error);
      
      return false;
    }
  }

  /**
   * Validate if postcode belongs to a delivery region
   * @param {string} postcode - The postcode to validate
   * @returns {boolean|null} - Whether the postcode is in a valid region, or null if in Waddeneilanden
   */
  static validatePostcodeRegion(postcode) {
    try {
      logMessage('log', `Starting postcode region validation for: ${postcode}`);
      
      
      
      // Get regions data from the script tag
      const regionsDataElement = document.getElementById('regions-data');
      if (!regionsDataElement) {
        logMessage('error', 'Regions data element not found');
        return false;
      }
      
      const regionsText = regionsDataElement.textContent;
      if (!regionsText) {
        logMessage('error', 'Regions data is empty');
        return false;
      }
      
      const regions = JSON.parse(regionsText);
      const normalizedPostcode = postcode.replace(/\s/g, '').toUpperCase();
      
      logMessage('log', `Loaded ${regions.length} regions for validation`);
      
      // Find matching region for the postcode
      let matchingRegion = null;
      let fallbackRegion = null;
      
      for (const region of regions) {
        // Check if region has postcodes
        if (region.postcodes) {
          
          // Check if this is a fallback region (single asterisk)
          const postcodesArray = this.normalizePostcodesArray(region.postcodes);
          if (postcodesArray.length === 1 && postcodesArray[0] === '*') {
            fallbackRegion = region;
            logMessage('log', `Found fallback region: ${region.system?.handle}`);
            continue; // Skip this region for now, we'll use it as fallback later
          }
          
          // Check if postcode belongs to this region
          if (this.postcodeBelongsToRegion(normalizedPostcode, region)) {
            matchingRegion = region;
            logMessage('log', `Found matching region: ${region.system?.handle}`);
            break;
          }
        }
      }
      
      // If we found a matching region, check if the postal code is also in Waddeneilanden
      if (matchingRegion) {
        const waddeneilandenRegion = this.findRegionByHandle(regions, "waddeneilanden");
        
        if (waddeneilandenRegion && this.postcodeBelongsToRegion(normalizedPostcode, waddeneilandenRegion)) {
          logMessage('log', 'Postcode is in Waddeneilanden region - returning null (no delivery)');
          
          
          
          return null;
        }
      }
      
      // If no specific region found, use fallback region
      if (!matchingRegion && fallbackRegion) {
        logMessage('log', 'No specific region matched, using fallback region');
        matchingRegion = fallbackRegion;
      }
      
      // Check if the region has timeslots available
      if (matchingRegion) {
        const tijdsloten = matchingRegion.tijdsloten?.value || matchingRegion.tijdsloten;
        const hasTimeslots = tijdsloten && tijdsloten.length > 0;
        
        logMessage('log', `Region ${matchingRegion.system?.handle} has timeslots: ${hasTimeslots}`);
        
        // Check if the region has timeslots available
        if (hasTimeslots) {
          
          
          return true;
        }
        
        logMessage('log', 'Region found but no timeslots available');
        return false;
      }
      
      logMessage('log', 'No matching region found for postcode');
      return false;
      
    } catch (error) {
      logMessage('error', 'Error validating postcode region', error);
      
      return false;
    }
  }

  /**
   * Validate postcode format (Dutch format: 1234 AB)
   * @param {string} postcode - The postcode to validate
   * @returns {boolean} - Whether the postcode format is valid
   */
  static validatePostcodeFormat(postcode) {
    try {
      const normalizedPostcode = postcode.replace(/\s/g, '').toUpperCase();
      const postcodeRegex = /^[1-9][0-9]{3}[A-Z]{2}$/;
      const isValid = postcodeRegex.test(normalizedPostcode);
      
      logMessage('log', `Postcode format validation for "${postcode}": ${isValid}`);
      return isValid;
    } catch (error) {
      logMessage('error', 'Error validating postcode format', error);
      
      return false;
    }
  }

  /**
   * Normalize postcode to standard format (1234 AB)
   * @param {string} postcode - The postcode to normalize
   * @returns {string} - Normalized postcode
   */
  static normalizePostcode(postcode) {
    try {
      const normalized = postcode.replace(/\s/g, '').toUpperCase();
      if (normalized.length === 6) {
        const formatted = `${normalized.slice(0, 4)} ${normalized.slice(4)}`;
        logMessage('log', `Normalized postcode: "${postcode}" -> "${formatted}"`);
        return formatted;
      }
      logMessage('log', `Postcode normalization: "${postcode}" -> "${postcode}" (no change needed)`);
      return postcode;
    } catch (error) {
      logMessage('error', 'Error normalizing postcode', error);
      
      return postcode;
    }
  }

  /**
   * Get the region for a given postal code
   * @param {string} postcode - The postal code to find the region for
   * @returns {{system?: {handle?: string}, postcodes?: any}|null} - The region object or null
   */
  static getRegionForPostcode(postcode) {
    try {
      logMessage('log', `Getting region for postcode: ${postcode}`);
      
      // Get regions data from the script tag
      const regionsDataElement = document.getElementById('regions-data');
      if (!regionsDataElement) {
        logMessage('error', 'Regions data element not found');
        return null;
      }
      
      const regionsText = regionsDataElement.textContent;
      if (!regionsText) {
        logMessage('error', 'Regions data is empty');
        return null;
      }
      
      const regions = JSON.parse(regionsText);
      const normalizedPostcode = postcode.replace(/\s/g, '').toUpperCase();
      
      logMessage('log', `Loaded ${regions.length} regions for region lookup`);
      
      // Find matching region for the postcode
      let matchingRegion = null;
      let fallbackRegion = null;
      
      for (const region of regions) {
        // Check if region has postcodes
        if (region.postcodes) {
          
          // Check if this is a fallback region (single asterisk)
          const postcodesArray = this.normalizePostcodesArray(region.postcodes);
          if (postcodesArray.length === 1 && postcodesArray[0] === '*') {
            fallbackRegion = region;
            logMessage('log', `Found fallback region: ${region.system?.handle}`);
            continue; // Skip this region for now, we'll use it as fallback later
          }
          
          // Check if postcode belongs to this region
          if (this.postcodeBelongsToRegion(normalizedPostcode, region)) {
            matchingRegion = region;
            logMessage('log', `Found matching region: ${region.system?.handle}`);
            break;
          }
        }
      }
      
      // If we found a matching region, check if the postal code is also in Waddeneilanden
      if (matchingRegion) {
        const waddeneilandenRegion = this.findRegionByHandle(regions, "waddeneilanden");
        
        if (waddeneilandenRegion && this.postcodeBelongsToRegion(normalizedPostcode, waddeneilandenRegion)) {
          logMessage('log', 'Postcode is in Waddeneilanden region - returning null');
          return null;
        }
      }
      
      // If no specific region found, use fallback region
      if (!matchingRegion && fallbackRegion) {
        logMessage('log', 'No specific region matched, using fallback region');
        matchingRegion = fallbackRegion;
      }
      
      logMessage('log', `Region lookup result: ${matchingRegion ? matchingRegion.system?.handle : 'null'}`);
      return matchingRegion;
      
    } catch (error) {
      logMessage('error', 'Error getting region for postcode', error);
      
      return null;
    }
  }

  /**
   * Check if two postal codes belong to the same region
   * @param {string} postcode1 - First postal code
   * @param {string} postcode2 - Second postal code
   * @returns {boolean} - Whether both postal codes belong to the same region
   */
  static arePostcodesInSameRegion(postcode1, postcode2) {
    try {
      if (!postcode1 || !postcode2) {
        logMessage('warn', 'One or both postal codes are empty:', { postcode1, postcode2 });
        return false;
      }
      
      logMessage('log', 'Starting region comparison for:', { postcode1, postcode2 });
      
      
      
      const region1 = this.getRegionForPostcode(postcode1);
      const region2 = this.getRegionForPostcode(postcode2);
      
      logMessage('log', 'Found regions:', {
        postcode1,
        region1: region1 ? { handle: region1.system?.handle } : 'null',
        postcode2,
        region2: region2 ? { handle: region2.system?.handle } : 'null'
      });
      
      // If either region is null (Waddeneilanden), they're not in the same region
      if (!region1 || !region2) {
        logMessage('log', 'One or both regions are null (Waddeneilanden)');
        return false;
      }
      
      // Compare region handles to determine if they're the same
      const areSame = region1.system?.handle === region2.system?.handle;
      logMessage('log', `Regions are same: ${areSame} (comparing handles: ${region1.system?.handle} vs ${region2.system?.handle})`);
      
      
      
      return areSame;
    } catch (error) {
      logMessage('error', 'Error comparing postal codes for same region', error);
      
      return false;
    }
  }
}

// Make it available globally
/** @type {any} */ (window).PostalCodeValidator = PostalCodeValidator; 